import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { ProcessService } from '../services/processService';
import { Process } from '../types';

export const AdminProcessesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    flatRateCost: '',
    sqftCost: '',
    isActive: true,
    order: '0',
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadProcesses();
  }, [user, navigate]);

  const loadProcesses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ProcessService.getProcesses();
      setProcesses(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as any;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);

      const processData = {
        name: formData.name,
        description: formData.description,
        flatRateCost: formData.flatRateCost ? parseFloat(formData.flatRateCost) : undefined,
        sqftCost: formData.sqftCost ? parseFloat(formData.sqftCost) : undefined,
        isActive: formData.isActive,
        order: parseInt(formData.order) || 0,
      };

      if (editingId) {
        await ProcessService.updateProcess(editingId, processData as any);
      } else {
        await ProcessService.createProcess(processData as any);
      }

      await loadProcesses();
      resetForm();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (process: Process) => {
    setEditingId(process.id);
    setFormData({
      name: process.name,
      description: process.description,
      flatRateCost: process.flatRateCost?.toString() || '',
      sqftCost: process.sqftCost?.toString() || '',
      isActive: process.isActive,
      order: process.order?.toString() || '0',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this process?')) return;
    try {
      await ProcessService.deleteProcess(id);
      await loadProcesses();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      flatRateCost: '',
      sqftCost: '',
      isActive: true,
      order: '0',
    });
    setShowForm(false);
  };

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p>Loading processes...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Manage Processes</h1>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          style={{
            padding: '10px 16px',
            background: 'var(--primary-button)',
            color: 'var(--primary-button-text)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          + Add Process
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#c33', marginBottom: '20px', borderRadius: '6px' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div style={{ background: 'white', padding: '20px', marginBottom: '24px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label>Name*</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Flat Rate Cost ($)</label>
                <input type="number" name="flatRateCost" value={formData.flatRateCost} onChange={handleChange} step="0.01" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} placeholder="e.g., 50.00" />
              </div>

              <div>
                <label>Square Footage Cost ($)</label>
                <input type="number" name="sqftCost" value={formData.sqftCost} onChange={handleChange} step="0.01" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} placeholder="e.g., 5.00" />
              </div>

              <div>
                <label>Order</label>
                <input type="number" name="order" value={formData.order} onChange={handleChange} min="1" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label>Description</label>
              <textarea name="description" value={formData.description} onChange={handleChange} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', minHeight: '80px' }} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" style={{ padding: '10px 16px', background: 'var(--primary-button)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                {editingId ? 'Update' : 'Create'} Process
              </button>
              <button type="button" onClick={resetForm} style={{ padding: '10px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Flat Rate</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Square Footage</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Active</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {processes.map((process) => (
              <tr key={process.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 16px' }}>{process.name}</td>
                <td style={{ padding: '12px 16px' }}>
                  ${process.flatRateCost || '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  ${process.sqftCost || '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>{process.isActive ? '✓' : '✗'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <button onClick={() => handleEdit(process)} style={{ padding: '6px 12px', marginRight: '8px', background: '#dbeafe', color: '#1e40af', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(process.id)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </AppLayout>
  );
};
